import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { supabase } from '../lib/supabase'

export default function AdminReports() {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [period, setPeriod] = useState('7') // days

  useEffect(() => { loadStats() }, [period])

  const loadStats = async () => {
    setLoading(true)
    const since = new Date()
    since.setDate(since.getDate() - parseInt(period))
    const sinceStr = since.toISOString()

    const [cashRes, harvestRes, invoiceRes, farmRes, userRes] = await Promise.all([
      supabase.from('cash_logs').select('total_income, total_expenses, net, log_date').gte('log_date', sinceStr.split('T')[0]).order('log_date'),
      supabase.from('harvest_logs').select('total_kg, grade_a_kg, grade_b_kg, reject_kg, harvest_date, crop').gte('harvest_date', sinceStr.split('T')[0]).order('harvest_date'),
      supabase.from('invoices').select('amount, status'),
      supabase.from('crop_log_sessions').select('id, log_date').gte('log_date', sinceStr.split('T')[0]),
      supabase.from('profiles').select('id, full_name, permission_level, is_active'),
    ])

    setStats({
      cash: cashRes.data || [],
      harvests: harvestRes.data || [],
      invoices: invoiceRes.data || [],
      farmLogs: farmRes.data || [],
      users: userRes.data || [],
    })
    setLoading(false)
  }

  const totalIncome = stats?.cash?.reduce((s, r) => s + (r.total_income||0), 0) || 0
  const totalExpenses = stats?.cash?.reduce((s, r) => s + (r.total_expenses||0), 0) || 0
  const netProfit = totalIncome - totalExpenses
  const totalHarvest = stats?.harvests?.reduce((s, r) => s + (r.total_kg||0), 0) || 0
  const gradeA = stats?.harvests?.reduce((s, r) => s + (r.grade_a_kg||0), 0) || 0
  const unpaidInvoices = stats?.invoices?.filter(i => i.status === 'Unpaid' || i.status === 'Overdue') || []
  const unpaidValue = unpaidInvoices.reduce((s, i) => s + (i.amount||0), 0)

  return (
    <AppLayout title="Reports">
      <div className="section-header">
        <div className="section-icon">📈</div>
        <div className="section-info">
          <h2>Business Reports</h2>
          <p>Overview of farm performance, financials, and operations.</p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Period:</span>
          {['7','14','30','90'].map(d => (
            <button key={d} onClick={() => setPeriod(d)} style={{ padding: '6px 12px', borderRadius: 6, fontSize: '0.78rem', cursor: 'pointer', border: `1.5px solid ${period===d?'var(--amber)':'var(--border)'}`, background: period===d?'var(--amber-pale)':'white', color: period===d?'var(--amber)':'var(--text-muted)', fontWeight: period===d?700:400, fontFamily: 'inherit' }}>
              {d}d
            </button>
          ))}
          <button className="btn btn-ghost btn-sm" onClick={() => window.print()}>🖨️ Print</button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><span className="spinner dark" /></div>
      ) : (
        <>
          {/* Financial KPIs */}
          <div style={{ marginBottom: 6, fontSize: '0.72rem', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)' }}>Financial Summary — Last {period} Days</div>
          <div className="kpi-grid" style={{ marginBottom: 20 }}>
            <div className="kpi green"><div className="kpi-label">Total Income</div><div className="kpi-value" style={{ fontSize: '1.3rem' }}>UGX {totalIncome.toLocaleString()}</div></div>
            <div className="kpi"><div className="kpi-label">Total Expenses</div><div className="kpi-value" style={{ fontSize: '1.3rem' }}>UGX {totalExpenses.toLocaleString()}</div></div>
            <div className={`kpi ${netProfit >= 0 ? 'green' : 'red'}`}><div className="kpi-label">Net Profit / Loss</div><div className="kpi-value" style={{ fontSize: '1.3rem', color: netProfit >= 0 ? 'var(--green-mid)' : 'var(--red)' }}>{netProfit >= 0 ? '+' : ''}UGX {netProfit.toLocaleString()}</div></div>
            <div className={`kpi ${unpaidInvoices.length > 0 ? 'red' : ''}`}><div className="kpi-label">Outstanding Invoices</div><div className="kpi-value">{unpaidInvoices.length}</div><div className="kpi-note">UGX {unpaidValue.toLocaleString()} owed</div></div>
          </div>

          {/* Operations KPIs */}
          <div style={{ marginBottom: 6, fontSize: '0.72rem', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)' }}>Operations Summary — Last {period} Days</div>
          <div className="kpi-grid" style={{ marginBottom: 20 }}>
            <div className="kpi green"><div className="kpi-label">Total Harvest</div><div className="kpi-value">{totalHarvest.toFixed(1)}kg</div></div>
            <div className="kpi"><div className="kpi-label">Grade A Produce</div><div className="kpi-value">{gradeA.toFixed(1)}kg</div><div className="kpi-note">{totalHarvest > 0 ? Math.round(gradeA/totalHarvest*100) : 0}% of total</div></div>
            <div className="kpi"><div className="kpi-label">Farm Logs Completed</div><div className="kpi-value">{stats.farmLogs.length}</div><div className="kpi-note">out of {period} days</div></div>
            <div className="kpi"><div className="kpi-label">Active Users</div><div className="kpi-value">{stats.users.filter(u => u.is_active !== false).length}</div><div className="kpi-note">{stats.users.length} total accounts</div></div>
          </div>

          <div className="grid-2">
            {/* Daily Cash Summary */}
            <div className="card">
              <div className="card-title">Daily Cash Log</div>
              <div className="card-subtitle">Income vs expenses per day</div>
              {stats.cash.length === 0 ? (
                <div className="empty-state"><div className="empty-icon">💵</div><h3>No cash logs in this period</h3></div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Date</th><th>Income</th><th>Expenses</th><th>Net</th></tr></thead>
                    <tbody>
                      {stats.cash.slice(-10).reverse().map((r, i) => (
                        <tr key={i}>
                          <td style={{ fontSize: '0.78rem' }}>{new Date(r.log_date).toLocaleDateString('en-UG')}</td>
                          <td style={{ color: 'var(--green-mid)', fontWeight: 600 }}>{(r.total_income||0).toLocaleString()}</td>
                          <td style={{ color: 'var(--red)' }}>{(r.total_expenses||0).toLocaleString()}</td>
                          <td style={{ fontWeight: 700, color: r.net >= 0 ? 'var(--green-mid)' : 'var(--red)' }}>{r.net >= 0 ? '+' : ''}{(r.net||0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Harvest Summary by Crop */}
            <div className="card">
              <div className="card-title">Harvest by Crop</div>
              <div className="card-subtitle">Total harvested in this period</div>
              {stats.harvests.length === 0 ? (
                <div className="empty-state"><div className="empty-icon">🌾</div><h3>No harvests in this period</h3></div>
              ) : (() => {
                const byCrop = {}
                stats.harvests.forEach(h => {
                  const name = h.crop || 'Unknown'
                  if (!byCrop[name]) byCrop[name] = { total: 0, a: 0, b: 0, r: 0 }
                  byCrop[name].total += h.total_kg || 0
                  byCrop[name].a += h.grade_a_kg || 0
                  byCrop[name].b += h.grade_b_kg || 0
                  byCrop[name].r += h.reject_kg || 0
                })
                return (
                  <div className="table-wrap">
                    <table>
                      <thead><tr><th>Crop</th><th>Total (kg)</th><th>Grade A</th><th>Grade B</th><th>Reject</th></tr></thead>
                      <tbody>
                        {Object.entries(byCrop).map(([crop, d]) => (
                          <tr key={crop}>
                            <td style={{ fontWeight: 600 }}>{crop}</td>
                            <td style={{ fontWeight: 700 }}>{d.total.toFixed(2)}</td>
                            <td style={{ color: 'var(--green-mid)' }}>{d.a.toFixed(2)}</td>
                            <td style={{ color: 'var(--amber)' }}>{d.b.toFixed(2)}</td>
                            <td style={{ color: 'var(--red)' }}>{d.r.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              })()}
            </div>

            {/* Invoice Status */}
            <div className="card">
              <div className="card-title">Invoice Status</div>
              {(() => {
                const byStatus = {}
                stats.invoices.forEach(i => {
                  byStatus[i.status] = (byStatus[i.status] || 0) + 1
                })
                const colors = { Paid: 'var(--green-mid)', Unpaid: 'var(--amber)', Overdue: 'var(--red)', Disputed: 'var(--text-muted)' }
                return Object.entries(byStatus).map(([status, count]) => (
                  <div key={status} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ fontWeight: 600, color: colors[status] || 'var(--text)' }}>{status}</span>
                    <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '1.3rem', fontWeight: 700 }}>{count}</span>
                  </div>
                ))
              })()}
            </div>

            {/* User Overview */}
            <div className="card">
              <div className="card-title">Team Overview</div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Name</th><th>Role</th><th>Status</th></tr></thead>
                  <tbody>
                    {stats.users.map(u => (
                      <tr key={u.id}>
                        <td style={{ fontWeight: 600 }}>{u.full_name || '—'}</td>
                        <td><span className={`badge ${u.permission_level === 'admin' ? 'badge-admin' : u.permission_level === 'manager' ? 'badge-green' : 'badge-grey'}`}>{u.permission_level}</span></td>
                        <td><span className={`badge ${u.is_active !== false ? 'badge-green' : 'badge-red'}`}>{u.is_active !== false ? 'Active' : 'Inactive'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </AppLayout>
  )
}
